import { useFeatureFlagContext } from 'Contexts/featureFlagContext';
import { useFederalLimitsContext } from 'Contexts/federalLimitsContext';

/**
 * Federal (IRS) benefit limits, sourced from JVP and delivered on the /config
 * response (config.federal_limits).
 *
 * Returns the JVP value for the requested limit ONLY when the
 * `is_federal_limits_from_jvp_enabled` flag is on and the data is available.
 * Otherwise it returns `undefined` so callers keep using their existing source
 * (a Contentful field or a hardcoded default) — the switch-over stays gated behind
 * the flag until JVP's federal-limit values are confirmed correct.
 */
export type FederalLimitKey = 'medical_fsa' | 'dependent_care_fsa' | 'commuter';

export const useFederalLimit = (key: FederalLimitKey): number | undefined => {
  const { is_federal_limits_from_jvp_enabled: isEnabled } = useFeatureFlagContext();
  const federalLimits = useFederalLimitsContext();

  if (!isEnabled) {
    return undefined;
  }

  const constants = federalLimits?.constants;
  if (!constants) {
    return undefined;
  }

  switch (key) {
    case 'medical_fsa':
      return constants.fsa?.health_care;
    case 'dependent_care_fsa':
      return constants.fsa?.dependent_care;
    case 'commuter':
      // Transit and parking are separate IRS limits, currently the same dollar amount.
      return constants.commute?.limit_monthly_transit;
    default:
      return undefined;
  }
};
